'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { MagicLinkForm } from './MagicLinkForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

interface Comment {
  id: string;
  body: string;
  isOfficial: boolean | null;
  createdAt: Date | number | null;
  visitor?: { name: string | null; email: string } | null;
  member?: { name: string | null; email: string } | null;
}

interface CommentThreadProps {
  comments: Comment[];
  requestId: string;
  portalSlug: string;
  currentVisitorId: string | null;
}

function timeAgo(date: Date | number | null): string {
  if (!date) return '';
  const now = Date.now();
  const then = typeof date === 'number' ? date : date.getTime();
  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
}

export function CommentThread({
  comments: initialComments,
  requestId,
  portalSlug,
  currentVisitorId,
}: CommentThreadProps) {
  const [comments, setComments] = useState(initialComments);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showAuth, setShowAuth] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    if (!currentVisitorId) {
      setShowAuth(true);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch(
        `/api/portal/${portalSlug}/requests/${requestId}/comments`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: newComment.trim() }),
        }
      );

      if (res.status === 401) {
        setShowAuth(true);
        return;
      }

      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        setError(data.error || 'Failed to post comment');
        return;
      }

      const created = (await res.json()) as Comment;
      setComments((prev) => [...prev, created]);
      setNewComment('');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">
        Comments {comments.length > 0 && `(${comments.length})`}
      </h2>

      {/* Comment list */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="text-sm font-medium mb-1">No comments yet</p>
            <p className="text-xs text-muted-foreground">Be the first to share your thoughts.</p>
          </div>
        ) : (
          comments.map((comment) => {
            const authorName =
              comment.member?.name ||
              comment.member?.email ||
              comment.visitor?.name ||
              comment.visitor?.email ||
              'Anonymous';
            const isOfficial = comment.isOfficial || !!comment.member;

            return (
              <div
                key={comment.id}
                className={cn(
                  'rounded-xl border border-border/60 p-4 transition-all duration-200',
                  isOfficial ? 'border-primary/30 bg-primary/5' : 'bg-card hover:shadow-sm'
                )}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                    {authorName.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-medium">{authorName}</span>
                  {isOfficial && (
                    <span className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium">
                      Team
                    </span>
                  )}
                  {/* Date.now()-derived — server and client can land in
                      different buckets, so suppress the hydration mismatch.
                      Same reason as RequestCard's timestamp. */}
                  <span className="text-xs text-muted-foreground ml-auto" suppressHydrationWarning>
                    {timeAgo(comment.createdAt)}
                  </span>
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap">{comment.body}</p>
              </div>
            );
          })
        )}
      </div>

      {/* Add comment form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={currentVisitorId ? 'Add a comment...' : 'Sign in to comment...'}
          rows={3}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
        />
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting || !newComment.trim()}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-all duration-200 flex items-center gap-2"
          >
            {isSubmitting && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {isSubmitting ? 'Posting...' : 'Post Comment'}
          </button>
        </div>
      </form>

      {/* Auth dialog for unauthenticated users */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="max-w-sm sm:max-w-md">
          <DialogHeader className="text-center sm:text-center">
            <DialogTitle>Sign in to comment</DialogTitle>
            <DialogDescription>Enter your email to receive a magic link</DialogDescription>
          </DialogHeader>
          <MagicLinkForm portalSlug={portalSlug} />
          <button
            onClick={() => setShowAuth(false)}
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Cancel
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
